import {Component, Input, OnChanges, OnDestroy, OnInit, Signal, SimpleChanges, ChangeDetectionStrategy} from "@angular/core";
import {NavigationEnd, Router, RouterModule} from "@angular/router";
import {filter, Subject, take, takeUntil} from "rxjs";
import {LanguageSelectorComponent} from "../../../shared/components/language-selector/language-selector.component";
import {PlatformService} from "../../../services/platform.service";
import {getPathWithoutQueryAndHash} from "../../../shared/utils/url.utils";
import {ModalService} from "../../../services/modal.service";
import {TranslatePipe} from "@ngx-translate/core";
import {DeviceDetectService} from "../../../services/device-detect.service";

export type PlatformHeaderMode = "beforeLogin" | "afterLogin";

@Component({
  selector: "app-platform-header",
  templateUrl: "./platform-header.component.html",
  styleUrls: ["./platform-header.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    LanguageSelectorComponent,
    TranslatePipe
  ],
})
export class PlatformHeaderComponent implements OnInit, OnChanges, OnDestroy {
  readonly brandName!: Signal<string>;

  get epakPlatform(): string {
    return this.platformService.epakPlatform;
  }
  get paidPlatform(): string {
    return this.platformService.paidPlatform;
  }

  get epakTabLogoSrc(): string {
    return this.platformService.getEpakTabLogoSrc();
  }

  get wellviaTabLogoSrc(): string {
    return this.platformService.getWellviaTabLogoSrc();
  }

  @Input() mode: PlatformHeaderMode = "beforeLogin";

  isPaidFlow = false;
  hasHero = false;
  disabledTabs = false;
  isMobile = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    readonly platformService: PlatformService,
    private readonly modalService: ModalService,
    private readonly deviceDetectService: DeviceDetectService,
  ) {
    this.brandName = this.platformService.brandName;
  }

  /** Active platform tab state for the current header mode. */
  get activePaidFlow(): boolean {
    if (this.isAfterLogin) {
      return this.platformService.isPaidByLandingFlow();
    }
    return this.isPaidFlow;
  }

  get isAfterLogin(): boolean {
    return this.mode === "afterLogin";
  }

  get isSolid(): boolean {
    return this.isAfterLogin || !this.hasHero;
  }

  get canAccessWellvia(): boolean {
    return this.platformService.canAccessWellviaHub;
  }

  ngOnInit(): void {
    this.isMobile = this.deviceDetectService.isMobile;
    this.updateHeaderState();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.updateHeaderState());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["mode"]) {
      this.updateHeaderState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEpakTabClick(): void {
    if (!this.platformService.isPaidByLandingFlow() || this.platformService.isSwitching()) {
      return;
    }
    void this.platformService.switchToEpak();
  }

  onWellviaTabClick(): void {
    if (this.platformService.isPaidByLandingFlow() || this.platformService.isSwitching()) {
      return;
    }

    if (this.isAfterLogin) {
      this.modalService.showWellviaSwitchConfirmModal()
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (confirmed) {
            void this.platformService.switchToWellvia();
          }
        });
    }
  }

  onLogout(): void {
    this.modalService.showLogoutConfirmModal();
  }

  private updateHeaderState(): void {
    const path = getPathWithoutQueryAndHash(this.router.url);

    if (this.isAfterLogin) {
      this.hasHero = false;
      this.disabledTabs = false;
      return;
    }

    this.isPaidFlow = path === "/paid-services" || path.startsWith("/paid-services/");
    this.hasHero = path === "/welcome" || path === "/paid-services";
    this.disabledTabs = path === "/login/new-user" || path === "/login/biometrics-enroll";
  }
}

